/**
 * AndesSettingsForm - Andes configuration settings
 * @module components/starnexus/wizard/forms/AndesSettingsForm
 *
 */

import React, { useState, useEffect, useCallback } from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Autosuggest from '@amzn/awsui-components-console/autosuggest';
import Select from '@amzn/awsui-components-console/select';
import TokenGroup from '@amzn/awsui-components-console/token-group';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import type { AndesSettings } from '../../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

interface AndesSettingsFormProps {
  settings: AndesSettings;
  onChange: (settings: AndesSettings) => void;
  datasetName?: string;
  partitionType?: string;
}

export const AndesSettingsForm: React.FC<AndesSettingsFormProps> = ({
  settings,
  onChange,
  datasetName,
  partitionType,
}) => {
  const [providerOptions] = useState<{ value: string }[]>([]);
  const [providerStatus] = useState<'pending' | 'loading' | 'finished' | 'error'>('finished');
  const [providerValid, setProviderValid] = useState<boolean | null>(null);

  // Validate provider on blur
  const validateProvider = useCallback((name: string) => {
    if (!name) { setProviderValid(null); return; }
    apiFetch(apiUrl(`/api/andes/providers/search?name=${encodeURIComponent(name)}`), { ...apiFetchDefaults })
      .then(r => r.json())
      .then(data => setProviderValid(data.found === true))
      .catch(() => setProviderValid(null));
  }, []);

  const [newPartitionKey, setNewPartitionKey] = useState('');

  const validateTableName = (name: string): string | null => {
    if (!name) return 'Table name is required';
    if (name.length < 3) return 'Must be at least 3 characters';
    if (!/^[a-z][a-z0-9_]*$/.test(name)) return 'Lowercase letters, numbers, underscores only';
    return null;
  };

  const COMMON_PARTITION_KEYS = ['date', 'region', 'marketplace_id', 'warehouse_id', 'category'];

  // Auto-fill from dataset name and output config
  useEffect(() => {
    const updates: Partial<AndesSettings> = {};
    if (datasetName && !settings.tableName) {
      updates.tableName = datasetName.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').slice(0, 60);
    }
    if (!settings.provider) {
      updates.provider = 'starcatcher_origin_ddl';
    }
    if (partitionType && settings.partitionKeys.length === 0) {
      const keyMap: Record<string, string> = { TIME: 'snapshot_date', REGION_ID: 'region_id', MARKETPLACE_ID: 'marketplace_id' };
      if (keyMap[partitionType]) updates.partitionKeys = [keyMap[partitionType]];
    }
    if (Object.keys(updates).length) onChange({ ...settings, ...updates });
  }, [datasetName, partitionType]);

  const tableNameError = settings.tableName
    ? validateTableName(settings.tableName)
    : null;

  const handleChange = (field: keyof AndesSettings, value: any) => {
    onChange({ ...settings, [field]: value });
  };

  const handleAddPartitionKey = () => {
    if (newPartitionKey && !settings.partitionKeys.includes(newPartitionKey)) {
      onChange({
        ...settings,
        partitionKeys: [...settings.partitionKeys, newPartitionKey]
      });
      setNewPartitionKey('');
    }
  };

  const handleRemovePartitionKey = (key: string) => {
    onChange({
      ...settings,
      partitionKeys: settings.partitionKeys.filter((k) => k !== key)
    });
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h3">Andes Configuration</Header>}>
        <SpaceBetween size="l">
          <FormField
            label="Provider"
            description="The Andes data provider for this dataset"
            errorText={providerValid === false ? 'Provider not found or no access' : undefined}
            secondaryControl={
              providerValid === true ? <StatusIndicator type="success">Verified</StatusIndicator> :
              providerValid === false ? <StatusIndicator type="error">Not found</StatusIndicator> : null
            }
          >
            <Autosuggest
              value={settings.provider}
              onChange={({ detail }) => {
                handleChange('provider', detail.value);
                setProviderValid(null);
              }}
              onBlur={() => validateProvider(settings.provider)}
              options={providerOptions}
              placeholder="Type provider name, e.g. starcatcher_origin_ddl"
              enteredTextLabel={v => `Use: "${v}"`}
              empty="No providers found"
              loadingText="Loading providers..."
              statusType={providerStatus}
            />
          </FormField>

          <ColumnLayout columns={2}>
            <FormField
              label="Table name"
              description="The Andes table name"
              constraintText="Lowercase letters, numbers, and underscores only"
              errorText={tableNameError || undefined}
            >
              <Input
                value={settings.tableName}
                onChange={({ detail }) =>
                  handleChange(
                    'tableName',
                    detail.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                  )
                }
                placeholder="my_table_name"
              />
            </FormField>

            <FormField
              label="Table version"
              description="The version number for this table"
            >
              <Input
                value={String(settings.tableVersion)}
                onChange={({ detail }) =>
                  handleChange(
                    'tableVersion',
                    Math.max(1, parseInt(detail.value) || 1)
                  )
                }
                type="number"
                inputMode="numeric"
              />
            </FormField>
          </ColumnLayout>

          <FormField
            label="Partition keys"
            description="Keys used to partition the data in Andes"
          >
            <SpaceBetween size="s">
              <SpaceBetween size="xs" direction="horizontal">
                <Select
                  selectedOption={
                    newPartitionKey
                      ? { value: newPartitionKey, label: newPartitionKey }
                      : null
                  }
                  onChange={({ detail }) =>
                    setNewPartitionKey(detail.selectedOption.value || '')
                  }
                  options={COMMON_PARTITION_KEYS.filter(
                    (k) => !settings.partitionKeys.includes(k)
                  ).map((k) => ({ value: k, label: k }))}
                  placeholder="Select partition key"
                  filteringType="auto"
                />
                <Input
                  value={newPartitionKey}
                  onChange={({ detail }) => setNewPartitionKey(detail.value)}
                  placeholder="Or enter custom key"
                  onKeyDown={(e) =>
                    e.detail.key === 'Enter' && handleAddPartitionKey()
                  }
                />
              </SpaceBetween>

              {settings.partitionKeys.length > 0 && (
                <TokenGroup
                  items={settings.partitionKeys.map((k) => ({
                    label: k,
                    dismissLabel: `Remove ${k}`
                  }))}
                  onDismiss={({ detail }) =>
                    handleRemovePartitionKey(
                      settings.partitionKeys[detail.itemIndex]
                    )
                  }
                />
              )}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default AndesSettingsForm;
